import { useEffect, useMemo, useState } from 'react';
import { fetchThemeCategories } from '@/lib/themeCategories';
import {
  fetchThemeReferenceFiles,
  getReferencePages,
  type ThemeReferenceFileRecord,
} from '@/lib/themeReferenceFiles';
import { THEME_BUSINESS_CATEGORIES } from '@/lib/themeBusinessCategories';

interface ThemeCategoryLite {
  id: number;
  titre: string;
}

export default function ReferenceSelectorPanel({
  referenceFiles,
  selectedId,
  onSelect,
  loading,
  error,
}: {
  referenceFiles: ThemeReferenceFileRecord[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  loading: boolean;
  error: string;
}) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-4">
        <i className="ri-book-open-line text-foreground-500"></i>
        <h3 className="text-sm font-semibold text-foreground-900">Fichier de référence</h3>
      </div>

      {error && (
        <div className="mb-3 px-3 py-2 rounded-md bg-red-50 border border-red-100 flex items-start gap-2">
          <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0 mt-0.5"></i>
          <span className="text-xs text-red-700">{error}</span>
        </div>
      )}

      <p className="text-[11px] text-foreground-400 mb-3">
        Sélectionne le fichier de référence qui définira les instructions et les pages à générer.
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <i className="ri-loader-4-line animate-spin text-foreground-400 text-xl"></i>
        </div>
      ) : referenceFiles.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-background-300/60 rounded-lg bg-background-100/40">
          <i className="ri-book-open-line text-3xl text-foreground-300 block mb-2"></i>
          <p className="text-xs text-foreground-500">Aucun fichier de référence</p>
          <p className="text-[11px] text-foreground-400 mt-1">Crée-en dans Super Admin → Fichiers de référence</p>
        </div>
      ) : (
        <div className="space-y-2 overflow-y-auto pr-1">
          {referenceFiles.map((ref) => {
            const isSelected = ref.id === selectedId;
            const pages = getReferencePages(ref.contenu);
            return (
              <button
                key={ref.id}
                onClick={() => onSelect(ref.id)}
                className={`w-full text-left rounded-lg border p-3 transition-colors cursor-pointer ${
                  isSelected
                    ? 'border-foreground-950 bg-background-100/60'
                    : 'border-background-200/70 bg-background-50 hover:border-background-300/60'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <i className={`text-sm flex-shrink-0 ${isSelected ? 'ri-file-text-fill text-foreground-900' : 'ri-file-text-line text-foreground-400'}`}></i>
                    <span className="text-xs font-medium text-foreground-900 truncate">{ref.titre}</span>
                  </div>
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-900 flex-shrink-0 whitespace-nowrap">
                    {pages.length} pages
                  </span>
                </div>
                {ref.description && (
                  <p className="text-[10px] text-foreground-500 mt-1.5 line-clamp-2">{ref.description}</p>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Hook utilitaire partagé : charge catégories + fichiers de référence. */
export function useGenerateContext() {
  const [categories, setCategories] = useState<ThemeCategoryLite[]>([]);
  const [referenceFiles, setReferenceFiles] = useState<ThemeReferenceFileRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [cats, refs] = await Promise.all([
          fetchThemeCategories(),
          fetchThemeReferenceFiles(),
        ]);
        if (!active) return;
        setCategories(cats.map((c) => ({ id: c.id, titre: c.titre })));
        setReferenceFiles(refs);
      } catch (err) {
        if (!active) return;
        setError(err instanceof Error ? err.message : 'Erreur de chargement');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const businessCategories = useMemo(() => THEME_BUSINESS_CATEGORIES, []);

  return { categories, referenceFiles, businessCategories, loading, error };
}